import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import adminApi from '../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Phone Accessories', 'Print', 'Soda', 'Juice', 'Snacks', 'Candy', 'Other'];

const emptyForm = { name: '', category: 'Other', price: '', cost: '', stock: '', photo_url: '', description: '', show_in_shop: false };

const Inventory = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('stock'); // 'stock' | 'showcase'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get('/products');
      setProducts(res.data || []);
    } catch { toast.error('Could not load products'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category, price: String(p.price), cost: String(p.cost || ''),
      stock: String(p.stock), photo_url: p.photo_url || '', description: p.description || '',
      show_in_shop: !!p.show_in_shop,
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await adminApi.post('/products/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, photo_url: res.data.url }));
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), category: form.category,
        price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0,
        stock: parseInt(form.stock) || 0, photo_url: form.photo_url,
        description: form.description, show_in_shop: form.show_in_shop,
      };
      if (editing) {
        const res = await adminApi.patch(`/products/${editing.id}`, payload);
        setProducts((prev) => prev.map((p) => p.id === editing.id ? res.data : p));
      } else {
        const res = await adminApi.post('/products', payload);
        setProducts((prev) => [...prev, res.data]);
      }
      toast.success('Saved');
      closeForm();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await adminApi.delete(`/products/${p.id}`);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const toggleShop = async (p) => {
    try {
      const res = await adminApi.patch(`/products/${p.id}`, { show_in_shop: !p.show_in_shop });
      setProducts((prev) => prev.map((x) => x.id === p.id ? res.data : x));
    } catch { toast.error('Toggle failed'); }
  };

  const inShop = products.filter((p) => p.show_in_shop);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 shadow-md border border-gray-200" data-testid="back-to-admin-btn">
            <i className="fas fa-arrow-left"></i> Back to Admin
          </button>
          <div className="flex gap-2">
            <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 shadow-md" data-testid="open-register-btn">
              <i className="fas fa-cash-register"></i> Open Register
            </button>
            <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 shadow-md" data-testid="add-product-btn">
              <i className="fas fa-plus"></i> Add product
            </button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3 mb-1"><i className="fas fa-boxes-stacked text-indigo-500"></i> Inventory</h1>
        <p className="text-gray-500 mb-6 text-sm">Manage what you have in stock and what customers can see in the online Shop.</p>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-2 mb-6 flex gap-1 w-fit" data-testid="inventory-tabs">
          <button onClick={() => setTab('stock')} className={`px-5 py-2 rounded-xl text-sm font-bold ${tab === 'stock' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-stock">
            <i className="fas fa-warehouse mr-2"></i>Stock ({products.length})
          </button>
          <button onClick={() => setTab('showcase')} className={`px-5 py-2 rounded-xl text-sm font-bold ${tab === 'showcase' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-showcase">
            <i className="fas fa-store mr-2"></i>Showcase ({inShop.length})
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-400"><i className="fas fa-spinner fa-spin text-2xl"></i></div>
        ) : tab === 'stock' ? (
          products.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center"><i className="fas fa-boxes-stacked text-5xl text-gray-300 mb-3"></i><p className="text-gray-500 font-semibold">No products yet</p><p className="text-gray-400 text-sm mt-1">Click &quot;Add product&quot; to get started.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stock-grid">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all" data-testid={`product-${p.id}`}>
                  {p.photo_url ? (
                    <div className="h-40 bg-gray-100 overflow-hidden"><img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" /></div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"><i className="fas fa-image text-4xl text-gray-300"></i></div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">{p.name}</p>
                        <p className="text-xs text-gray-400">{p.category}</p>
                      </div>
                      <p className="text-xl font-black text-indigo-600">€{p.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span className={`font-bold ${p.stock <= 5 ? 'text-red-500' : p.stock <= 10 ? 'text-amber-500' : 'text-emerald-600'}`}>Stock: {p.stock}</span>
                      {p.show_in_shop && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold"><i className="fas fa-eye"></i> In shop</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="flex-1 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg" data-testid={`edit-${p.id}`}><i className="fas fa-pencil mr-1"></i>Edit</button>
                      <button onClick={() => remove(p)} className="px-3 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-lg" data-testid={`del-${p.id}`}><i className="fas fa-trash"></i></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-2" data-testid="showcase-list">
            <p className="text-sm text-gray-500 mb-3">Toggle which products appear on the customer <strong>/shop</strong> page.</p>
            {products.map((p) => (
              <div key={p.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
                {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center"><i className="fas fa-image text-gray-300"></i></div>}
                <div className="flex-1 min-w-0"><p className="font-bold text-gray-800 truncate">{p.name}</p><p className="text-xs text-gray-400">{p.category} · €{p.price.toFixed(2)}</p></div>
                <label className="inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={p.show_in_shop} onChange={() => toggleShop(p)} className="sr-only peer" data-testid={`toggle-shop-${p.id}`} />
                  <div className="w-11 h-6 bg-gray-200 peer-checked:bg-emerald-500 rounded-full transition-colors relative">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${p.show_in_shop ? 'translate-x-5' : ''}`}></div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeForm}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-form-modal">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                <h3 className="font-bold text-lg">{editing ? 'Edit product' : 'New product'}</h3>
                <button onClick={closeForm} className="w-8 h-8 rounded-full hover:bg-gray-100"><i className="fas fa-times"></i></button>
              </div>
              <div className="p-5 space-y-3">
                {/* Photo */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Photo</label>
                  <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} className="hidden" />
                  {form.photo_url ? (
                    <div className="relative mt-1">
                      <img src={form.photo_url} alt="" className="w-full h-40 object-cover rounded-xl border border-gray-200" />
                      <button onClick={() => setForm((f) => ({ ...f, photo_url: '' }))} className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow"><i className="fas fa-times text-red-500"></i></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full mt-1 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-400 text-gray-500 hover:text-indigo-600" data-testid="upload-photo-btn">
                      {uploading ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</> : <><i className="fas fa-cloud-arrow-up text-2xl block mb-1"></i> Upload photo</>}
                    </button>
                  )}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="form-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="form-category">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Stock</label>
                    <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="form-stock" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Sell price (€)</label>
                    <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="form-price" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Cost (€, optional)</label>
                    <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Description (optional)</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="2" className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg resize-none"></textarea>
                </div>
                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 cursor-pointer">
                  <input type="checkbox" checked={form.show_in_shop} onChange={(e) => setForm({ ...form, show_in_shop: e.target.checked })} className="w-5 h-5 accent-emerald-500" data-testid="form-show-in-shop" />
                  <div><p className="font-bold text-gray-800">Show in customer shop</p><p className="text-xs text-gray-500">Visible on the public /shop page</p></div>
                </label>
              </div>
              <div className="p-5 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
                <button onClick={closeForm} className="px-4 py-2 text-gray-600 font-semibold">Cancel</button>
                <button onClick={save} disabled={saving} className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-50" data-testid="form-save-btn">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
